// The egg-pack purchase celebration, ported from the Celebration Lab (design
// locked 2026-09-01, tuned on localhost review): exactly one particle per egg
// granted streams from BEHIND the pack's buy button (a stay-gold clone with
// the price label rides above the stream), swells gently mid-flight, and
// lands on the balance chip's egg icon. The chip gets the gold glow + a pulse
// per landed egg; a quiet toast confirms the purchase immediately; the card
// rumbles while its own count drains to zero, then restocks with a 5x-speed
// count-up. During the stream every pack card is inert (no hover, no clicks).
//
// Particles draw on a full-viewport fixed canvas so the shop modal never
// clips the flight. Reduced-motion snaps instantly.

import musicController from "../../audio/music-controller";
import type { SoundId } from "../../audio/sounds";

export const RM = typeof matchMedia !== "undefined" && matchMedia("(prefers-reduced-motion: reduce)").matches;

interface Particle {
  sx: number; sy: number; cx: number; cy: number; tx: number; ty: number;
  t0: number; dur: number; s: number;
  arrived: boolean;
}

// Smallish out of the button, gentle mid-air swell, small into the chip.
export const sizeProfile = (k: number) => 0.42 + 0.5 * Math.sin(Math.PI * Math.min(1, k));

export function eggSprite(): HTMLCanvasElement {
  const c = document.createElement("canvas");
  c.width = 40; c.height = 48;
  const ctx = c.getContext("2d")!;
  const g = ctx.createRadialGradient(14, 13, 2, 20, 24, 26);
  g.addColorStop(0, "#ffe9a8"); g.addColorStop(0.65, "#d9a531"); g.addColorStop(1, "#8a5f14");
  ctx.fillStyle = g;
  ctx.beginPath(); ctx.ellipse(20, 26, 15, 20, 0, 0, Math.PI * 2); ctx.fill();
  return c;
}

// Up to this many eggs fly close together and each lands with its own note
// (the caller then skips the "eggs" sound); more stream out over seconds with
// the one "eggs" sound the caller plays.
export const FEW_EGGS = 10;
const FEW_GAP_MS = 85;
// How long `total` eggs take to leave (the first to the last).
export const streamMsFor = (total: number) => (total <= FEW_EGGS ? (total - 1) * FEW_GAP_MS : Math.min(4200, 900 + total * 1.6));

export interface CelebrationHooks {
  /** The pack's card slot ([data-packid]); null = skip card-local effects. */
  slot: HTMLElement | null;
  /** Eggs granted; also the exact particle count. */
  total: number;
  /** Shown on the stay-gold button clone (the price, e.g. "$4.99"). */
  buttonLabel?: string;
  /** Shown in the confirmation toast (e.g. "Nest"). */
  packName?: string;
  /** Called once per landed egg with the running landed count. */
  onProgress: (landed: number) => void;
  /** Where the eggs come from instead of a pack's buy button (the profile's
   *  opened achievements): each element sends its `count`. No toast then. */
  sources?: { el: HTMLElement; count: number }[];
  /** Where they land instead of the egg chip ([data-eggchip]). */
  target?: HTMLElement | null;
  /** Every landing has its note also when many fly (opened achievements):
   *  they climb the scale over the stream, at most one every 45 ms. */
  notes?: boolean;
}

export function runEggCelebration({ slot, total, buttonLabel, packName, onProgress, sources, target, notes }: CelebrationHooks): Promise<void> {
  const chip = target ?? document.querySelector<HTMLElement>("[data-eggchip]");
  const chipEgg = chip?.querySelector("canvas") ?? chip;
  const few = total <= FEW_EGGS;
  if (!chipEgg || total <= 0 || RM) {
    if (few && total > 0) musicController.play("egg-coin");
    onProgress(total); return Promise.resolve();
  }

  const cr = chipEgg.getBoundingClientRect();
  const tx = cr.left + cr.width / 2, ty = cr.top + cr.height / 2;

  // --- particle canvas (full viewport, above everything) ---
  const pc = document.createElement("canvas");
  const DPR = Math.min(2, window.devicePixelRatio || 1);
  pc.width = innerWidth * DPR; pc.height = innerHeight * DPR;
  pc.style.cssText = "position:fixed; inset:0; width:100%; height:100%; pointer-events:none; z-index:2000;";
  document.body.appendChild(pc);
  const ctx = pc.getContext("2d")!;
  const sprite = eggSprite();

  // --- every pack card goes inert for the duration (no hover, no clicks) ---
  const slots = Array.from(document.querySelectorAll<HTMLElement>("[data-packid]"));
  for (const s of slots) {
    s.style.pointerEvents = "none";
    if (s !== slot) { s.style.transition = "opacity .3s"; s.style.opacity = "0.55"; }
  }

  // --- source: behind the buy button; a stay-gold clone with the price ---
  const btn = slot?.querySelector<HTMLElement>("[data-buy]") ?? null;
  let sx = tx, sy = innerHeight - 80, jitX = 30, overlay: HTMLElement | null = null;
  if (btn) {
    const br = btn.getBoundingClientRect();
    sx = br.left + br.width / 2; sy = br.top + 4; jitX = Math.max(10, br.width / 2 - 14);
    overlay = document.createElement("div");
    overlay.textContent = buttonLabel || btn.textContent;
    overlay.style.cssText = `position:fixed; left:${br.left}px; top:${br.top}px; width:${br.width}px; height:${br.height}px; z-index:2001; display:flex; align-items:center; justify-content:center; font-family:Orbitron,sans-serif; font-weight:800; font-size:14px; border-radius:9px; background:#ffcf4d; color:#3a2600; border:1px solid #ffcf4d; pointer-events:none; font-variant-numeric:tabular-nums;`;
    document.body.appendChild(overlay);
  }

  // --- the chip is the spotlight: gold glow + scale (no page dim) ---
  const hb = (chip as HTMLElement).getBoundingClientRect();
  const halo = document.createElement("div");
  halo.style.cssText = `position:fixed; left:${hb.right - 120}px; top:${hb.top - 40}px; width:150px; height:120px; background:radial-gradient(ellipse at 62% 34%, rgba(255,207,77,0.16), transparent 42%); opacity:0; transition:opacity .4s; pointer-events:none; z-index:2000;`;
  document.body.appendChild(halo);
  requestAnimationFrame(() => { halo.style.opacity = "1"; });
  if (chip) {
    chip.style.transition = "transform .3s cubic-bezier(.2,1.3,.4,1), border-color .3s, box-shadow .3s";
    chip.style.transform = "scale(1.14)";
    chip.style.borderColor = "rgba(255,207,77,0.9)";
    chip.style.boxShadow = "0 0 16px rgba(255,207,77,0.4)";
    chip.style.zIndex = "2002"; chip.style.position = "relative";
  }

  // --- the sources' own starts (the profile): one start per egg, spread over
  //     the elements by their counts ---
  const starts: { x: number; y: number; jit: number }[] = [];
  for (const s of sources || []) {
    const r = s.el.getBoundingClientRect();
    const x = Math.max(20, Math.min(innerWidth - 20, r.left + r.width / 2));
    const y = Math.max(20, Math.min(innerHeight - 20, r.top + r.height / 2));
    for (let i = 0; i < s.count; i++) starts.push({ x, y, jit: Math.max(10, r.width / 2 - 20) });
  }
  // (top to bottom: the stream runs down the cards)
  if (starts.length) starts.sort((a, b) => a.y - b.y);

  // --- the quiet toast: instant purchase receipt, bottom center ---
  const toast = document.createElement("div");
  toast.innerHTML = `<span style="width:14px; height:17px; border-radius:50% 50% 50% 50% / 60% 60% 42% 42%; background:radial-gradient(circle at 35% 30%, #ffe9a8, #d9a531 65%, #8a5f14); display:inline-block;"></span><span>${packName ? packName + " purchase complete" : "Purchase complete"}</span><span style="font-family:Orbitron,sans-serif; color:#ffcf4d; font-weight:800; font-size:13px;">+${total.toLocaleString()}</span>`;
  // Taller and, on desktop, well clear of the bottom edge so the receipt
  // reads as its own moment rather than hugging the browser chrome.
  const toastRest = matchMedia("(min-width: 700px)").matches ? "64px" : "20px";
  toast.style.cssText = "position:fixed; left:50%; bottom:-90px; transform:translateX(-50%); display:flex; align-items:center; gap:12px; background:#0e1422; color:#eaf0ff; border:1px solid rgba(70,217,126,0.4); border-radius:14px; padding:15px 22px; box-shadow:0 10px 30px rgba(0,0,0,0.5); transition:bottom .45s cubic-bezier(.2,1.4,.4,1); white-space:nowrap; font-size:14.5px; font-weight:600; z-index:2003;";
  if (!sources) {
    document.body.appendChild(toast);
    requestAnimationFrame(() => { toast.style.bottom = toastRest; });
  }

  // --- card rumble + the card's own count draining to zero ---
  const pack = slot?.querySelector<HTMLElement>("[data-pack]") ?? null;
  const amtEl = slot?.querySelector<HTMLElement>("[data-amt]") ?? null;
  const wellVis = slot?.querySelector<HTMLElement>("[data-well] canvas") ?? null;
  const styleTag = document.createElement("style");
  styleTag.textContent = "@keyframes ar-egg-rumble { 0% { transform:translate(0.6px,-0.4px) rotate(0.15deg);} 25% { transform:translate(-0.7px,0.5px) rotate(-0.2deg);} 50% { transform:translate(0.5px,0.6px) rotate(0.1deg);} 75% { transform:translate(-0.5px,-0.6px) rotate(-0.1deg);} 100% { transform:translate(0.6px,-0.4px) rotate(0.15deg);} }";
  document.head.appendChild(styleTag);
  if (pack) pack.style.animation = "ar-egg-rumble .12s linear infinite";
  if (overlay) overlay.style.animation = "ar-egg-rumble .12s linear infinite";

  const streamMs = streamMsFor(total);
  const t0 = performance.now();
  let drainRaf = 0;
  const drain = (t: number) => {
    const k = Math.min(1, (t - t0) / streamMs);
    if (amtEl) amtEl.textContent = Math.round(total * (1 - k)).toLocaleString();
    if (wellVis) wellVis.style.opacity = String(1 - 0.7 * k);
    if (k < 1) drainRaf = requestAnimationFrame(drain);
  };
  drainRaf = requestAnimationFrame(drain);

  // --- the stream: one particle per egg ---
  const parts: Particle[] = [];
  for (let i = 0; i < total; i++) {
    const st = starts.length ? starts[Math.floor((i * starts.length) / total) % starts.length] : null;
    const jx = st ? st.x + (Math.random() * 2 - 1) * st.jit : sx + (Math.random() * 2 - 1) * jitX;
    const jy = st ? st.y + (Math.random() * 2 - 1) * 8 : sy + (Math.random() * 2 - 1) * 3;
    parts.push({
      sx: jx, sy: jy, tx, ty,
      cx: (jx + tx) / 2 + (Math.random() * 260 - 130),
      cy: Math.min(jy, ty) - 60 - Math.random() * 140,
      // (a few eggs keep an even beat: same flight time, no start jitter)
      t0: t0 + (total === 1 ? 0 : (i / (total - 1)) * streamMs) + (few ? 0 : Math.random() * 30),
      dur: few ? 640 : 680 * (0.8 + Math.random() * 0.4),
      s: 0.32 + Math.random() * 0.5,
      arrived: false,
    });
  }

  return new Promise((resolve) => {
    let landed = 0, lastPulse = 0, lastNote = -1e9;
    function frame(now: number) {
      ctx.clearRect(0, 0, pc.width, pc.height);
      let alive = false;
      for (const pt of parts) {
        if (pt.arrived) continue;
        const k = (now - pt.t0) / pt.dur;
        if (k < 0) { alive = true; continue; }
        if (k >= 1) {
          pt.arrived = true; landed++; onProgress(landed);
          if (few) musicController.play(`egg-land-${Math.min(landed, FEW_EGGS) - 1}` as SoundId);
          else if (notes && now - lastNote >= 45) {
            lastNote = now;
            musicController.play(`egg-land-${Math.min(FEW_EGGS - 1, Math.floor(((landed - 1) / total) * FEW_EGGS))}` as SoundId);
          }
          if (now - lastPulse > 70 && chip) {
            lastPulse = now;
            chip.style.transform = "scale(1.2)";
            setTimeout(() => { chip.style.transform = "scale(1.14)"; }, 60);
          }
          continue;
        }
        alive = true;
        const u = 1 - k;
        const x = u * u * pt.sx + 2 * u * k * pt.cx + k * k * pt.tx;
        const y = u * u * pt.sy + 2 * u * k * pt.cy + k * k * pt.ty;
        const sc = pt.s * sizeProfile(k), a = k > 0.92 ? (1 - k) / 0.08 : 1;
        ctx.globalAlpha = a;
        ctx.drawImage(sprite, (x - sc * 10) * DPR, (y - sc * 12) * DPR, sc * 20 * DPR, sc * 24 * DPR);
      }
      ctx.globalAlpha = 1;
      if (alive) { requestAnimationFrame(frame); return; }

      // --- stream done: restock the card with a 5x-speed count-up ---
      cancelAnimationFrame(drainRaf);
      if (pack) pack.style.animation = "";
      if (overlay) overlay.style.animation = "";
      const restockMs = Math.max(220, streamMs / 5);
      const r0 = performance.now();
      const restock = (t: number) => {
        const k = Math.min(1, (t - r0) / restockMs);
        if (amtEl) amtEl.textContent = Math.round(total * k).toLocaleString();
        if (wellVis) wellVis.style.opacity = String(0.3 + 0.7 * k);
        if (k < 1) { requestAnimationFrame(restock); return; }
        if (wellVis) wellVis.style.opacity = "";
        finish();
      };
      requestAnimationFrame(restock);

      function finish() {
        overlay?.remove();
        halo.style.opacity = "0";
        if (chip) { chip.style.transform = ""; chip.style.borderColor = ""; chip.style.boxShadow = ""; }
        setTimeout(() => { toast.style.bottom = "-90px"; setTimeout(() => toast.remove(), 500); }, 2500);
        setTimeout(() => {
          pc.remove(); halo.remove(); styleTag.remove();
          if (chip) { chip.style.zIndex = ""; chip.style.position = ""; }
          for (const s of slots) { s.style.pointerEvents = ""; s.style.opacity = ""; }
        }, 500);
        resolve();
      }
    }
    requestAnimationFrame(frame);
  });
}
